import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useCartStore } from '../store/cart';
const SHIPPING_FLAT = 8;
interface FormState {
  email: string;
  fullName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  card: string;
  expiry: string;
  cvc: string;
}
const emptyForm: FormState = {
  email: '',
  fullName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  card: '',
  expiry: '',
  cvc: ''
};
export function Checkout() {
  const navigate = useNavigate();
  const {
    items,
    totalPrice,
    clearCart,
    closeCart
  } = useCartStore();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [processing, setProcessing] = useState(false);
  const subtotal = totalPrice();
  const shipping = subtotal > 0 ? SHIPPING_FLAT : 0;
  const total = subtotal + shipping;
  const setField = (key: keyof FormState, value: string) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email.';
    if (!form.fullName.trim()) next.fullName = 'Required.';
    if (!form.address.trim()) next.address = 'Required.';
    if (!form.city.trim()) next.city = 'Required.';
    if (!form.state.trim()) next.state = 'Required.';
    if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) next.zip = 'Enter a valid ZIP.';
    if (form.card.replace(/\s/g, '').length < 15) next.card = 'Enter a valid card number.';
    if (!/^\d{2}\/\d{2}$/.test(form.expiry.trim())) next.expiry = 'MM/YY';
    if (!/^\d{3,4}$/.test(form.cvc.trim())) next.cvc = '3–4 digits.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setProcessing(true);
    const purchasedItems = items.map(i => ({
      name: i.product.name,
      color: i.color,
      size: i.size,
      quantity: i.quantity,
      price: i.product.price
    }));
    await new Promise(r => setTimeout(r, 1400));
    const orderNumber = 'VSC-' + Math.random().toString(36).slice(2, 8).toUpperCase();
    clearCart();
    closeCart();
    navigate('/order-confirmation', {
      state: {
        orderNumber,
        items: purchasedItems,
        total,
        email: form.email
      },
      replace: true
    });
  };
  if (items.length === 0) {
    return <div className="py-32 px-4 text-center max-w-xl mx-auto" data-id="element-101">
        <h1 className="font-display text-4xl tracking-wide mb-4" data-id="element-102">
          Your cart is empty
        </h1>
        <p className="text-neutral-400 mb-8" data-id="element-103">
          Add some pieces before heading to checkout.
        </p>
        <Link to="/shop" className="inline-block bg-vsc-gold text-black font-medium px-8 py-4 hover:bg-white transition-colors rounded-sm" data-id="element-104">
          Shop the Collection
        </Link>
      </div>;
  }
  const inputClass = (key: keyof FormState) => `w-full bg-neutral-900 border px-4 py-3 text-white focus:outline-none rounded-sm transition-colors ${errors[key] ? 'border-red-500/70 focus:border-red-500' : 'border-neutral-700 focus:border-vsc-gold'}`;
  return <div className="py-12 px-4 max-w-6xl mx-auto w-full" data-id="element-105">
      <Link to="/shop" className="inline-flex items-center text-neutral-400 hover:text-white mb-8 transition-colors" data-id="element-106">
        <ChevronLeft size={20} className="mr-1" data-id="element-107" />
        Continue Shopping
      </Link>

      <h1 className="font-display text-5xl tracking-wide mb-10" data-id="element-108">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12" data-id="element-109">
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-10" noValidate data-id="element-110">
          <section data-id="element-111">
            <h2 className="font-display text-2xl tracking-wide mb-4" data-id="element-112">
              Contact
            </h2>
            <div data-id="element-113">
              <input type="email" placeholder="Email address" value={form.email} onChange={e => setField('email', e.target.value)} className={inputClass('email')} aria-invalid={!!errors.email} data-id="element-114" />
              {errors.email && <p className="text-red-400 text-xs mt-1" data-id="element-115">{errors.email}</p>}
            </div>
          </section>

          <section data-id="element-116">
            <h2 className="font-display text-2xl tracking-wide mb-4" data-id="element-117">
              Shipping Address
            </h2>
            <div className="space-y-4" data-id="element-118">
              <div data-id="element-119">
                <input placeholder="Full name" value={form.fullName} onChange={e => setField('fullName', e.target.value)} className={inputClass('fullName')} data-id="element-120" />
                {errors.fullName && <p className="text-red-400 text-xs mt-1" data-id="element-121">{errors.fullName}</p>}
              </div>
              <div data-id="element-122">
                <input placeholder="Street address" value={form.address} onChange={e => setField('address', e.target.value)} className={inputClass('address')} data-id="element-123" />
                {errors.address && <p className="text-red-400 text-xs mt-1" data-id="element-124">{errors.address}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-125">
                <div className="sm:col-span-1" data-id="element-126">
                  <input placeholder="City" value={form.city} onChange={e => setField('city', e.target.value)} className={inputClass('city')} data-id="element-127" />
                  {errors.city && <p className="text-red-400 text-xs mt-1" data-id="element-128">{errors.city}</p>}
                </div>
                <div data-id="element-129">
                  <input placeholder="State" value={form.state} onChange={e => setField('state', e.target.value)} className={inputClass('state')} data-id="element-130" />
                  {errors.state && <p className="text-red-400 text-xs mt-1" data-id="element-131">{errors.state}</p>}
                </div>
                <div data-id="element-132">
                  <input placeholder="ZIP" value={form.zip} onChange={e => setField('zip', e.target.value)} className={inputClass('zip')} data-id="element-133" />
                  {errors.zip && <p className="text-red-400 text-xs mt-1" data-id="element-134">{errors.zip}</p>}
                </div>
              </div>
            </div>
          </section>

          <section data-id="element-135">
            <h2 className="font-display text-2xl tracking-wide mb-4" data-id="element-136">
              Payment
            </h2>
            <div className="space-y-4" data-id="element-137">
              <div data-id="element-138">
                <input inputMode="numeric" placeholder="Card number" value={form.card} onChange={e => setField('card', e.target.value)} className={inputClass('card')} data-id="element-139" />
                {errors.card && <p className="text-red-400 text-xs mt-1" data-id="element-140">{errors.card}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-141">
                <div data-id="element-142">
                  <input placeholder="MM/YY" value={form.expiry} onChange={e => setField('expiry', e.target.value)} className={inputClass('expiry')} data-id="element-143" />
                  {errors.expiry && <p className="text-red-400 text-xs mt-1" data-id="element-144">{errors.expiry}</p>}
                </div>
                <div data-id="element-145">
                  <input inputMode="numeric" placeholder="CVC" value={form.cvc} onChange={e => setField('cvc', e.target.value)} className={inputClass('cvc')} data-id="element-146" />
                  {errors.cvc && <p className="text-red-400 text-xs mt-1" data-id="element-147">{errors.cvc}</p>}
                </div>
              </div>
              <p className="text-xs text-neutral-500" data-id="element-148">
                This is a demo store — no real payment is processed.
              </p>
            </div>
          </section>

          <button type="submit" disabled={processing} className="w-full bg-vsc-gold text-black font-medium py-4 text-lg hover:bg-white transition-colors rounded-sm disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-149">
            {processing ? 'Processing…' : `Pay $${total}`}
          </button>
        </form>

        {/* Order Summary */}
        <aside className="bg-neutral-900/40 border border-neutral-800 rounded-sm p-6 h-fit lg:sticky lg:top-28" data-id="element-150">
          <h2 className="font-display text-2xl tracking-wide mb-6" data-id="element-151">
            Order Summary
          </h2>
          <div className="space-y-4 mb-6" data-id="element-152">
            {items.map(item => <div key={item.id} className="flex gap-3" data-id="element-153">
                <div className="w-14 h-16 bg-neutral-800 rounded-sm overflow-hidden flex-shrink-0" data-id="element-154">
                  <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-155" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-156">
                  <p className="text-sm font-medium leading-tight truncate" data-id="element-157">
                    {item.product.name}
                  </p>
                  <p className="text-xs text-neutral-400 mt-0.5" data-id="element-158">
                    {item.color} / {item.size} · Qty {item.quantity}
                  </p>
                </div>
                <p className="text-sm font-medium whitespace-nowrap" data-id="element-159">
                  ${item.product.price * item.quantity}
                </p>
              </div>)}
          </div>
          <div className="border-t border-neutral-800 pt-4 space-y-2 text-sm" data-id="element-160">
            <div className="flex justify-between text-neutral-400" data-id="element-161">
              <span data-id="element-162">Subtotal</span>
              <span className="text-white" data-id="element-163">${subtotal}</span>
            </div>
            <div className="flex justify-between text-neutral-400" data-id="element-164">
              <span data-id="element-165">Shipping</span>
              <span className="text-white" data-id="element-166">${shipping}</span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-neutral-800 mt-3" data-id="element-167">
              <span className="font-medium" data-id="element-168">Total</span>
              <span className="font-display text-2xl" data-id="element-169">${total}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>;
}